import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Code2, Play, Save } from 'lucide-react'
import { request } from '@/lib/request'
import { JsonView, allExpanded, darkStyles } from 'react-json-view-lite'
import 'react-json-view-lite/dist/index.css'
import { CodeEditor } from '@/components/CodeEditor'
import { toast } from 'sonner'

const CODE_CACHE_KEY = 'mock-vite:dev:test-code'
const DEFAULT_TEST_CODE = `req = auth.Auth('xr')
req.use('token3')
api = req.apis('detail')
resp = req.post(api)
print(resp)`

interface ApiItem {
  id: string
  desc: string
  path: string
  params?: Array<{
    param: string
    desc?: string
    type?: string
    default?: any
  }>
}

export default function Dev() {
  const [apis, setApis] = useState<ApiItem[]>([])
  const [currentApi, setCurrentApi] = useState<ApiItem | null>(null)
  const [formData, setFormData] = useState<Record<string, any>>({})
  const [result, setResult] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [testCode, setTestCode] = useState(() => {
    try {
      return localStorage.getItem(CODE_CACHE_KEY) ?? DEFAULT_TEST_CODE
    } catch {
      return DEFAULT_TEST_CODE
    }
  })

  useEffect(() => {
    loadApis()
  }, [])

  const loadApis = async () => {
    try {
      const resp = await request('/common/apis/v1/interface_system_apis', {
        page_size: 100,
        page_num: 1,
        query_filter: {},
      })
      if (resp && !resp.code) {
        setApis(resp.data)
      }
    } catch (error) {
      console.error('Failed to load apis:', error)
    }
  }

  const handleExecute = async () => {
    if (!currentApi) return

    setLoading(true)
    try {
      const resp = await request('/common/apis/v1' + currentApi.path, formData)
      setResult(resp)
    } catch (error) {
      setResult({ error: String(error) })
    } finally {
      setLoading(false)
    }
  }

  const handleApiChange = (apiId: string) => {
    const api = apis.find(a => a.id === apiId)
    setCurrentApi(api || null)

    // Reset form with default values
    if (api?.params) {
      const defaults: Record<string, any> = {}
      api.params.forEach(param => {
        if (param.default !== undefined) {
          defaults[param.param] = param.default
        }
      })
      setFormData(defaults)
    } else {
      setFormData({})
    }
    setResult({})
  }

  const handleFieldChange = (param: string, value: any) => {
    setFormData(prev => ({ ...prev, [param]: value }))
  }

  const handleCacheCode = () => {
    try {
      localStorage.setItem(CODE_CACHE_KEY, testCode)
      toast.success('代码已缓存到浏览器')
    } catch {
      toast.error('代码缓存失败，请检查浏览器存储权限')
    }
  }

  return (
    <div className="h-full p-6 overflow-auto space-y-6">
      <h1 className="text-2xl font-bold">开发调试工具</h1>

      <div className="grid grid-cols-12 gap-4">
        {/* Left Panel - API Selection and Parameters */}
        <div className="col-span-5 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Code2 className="h-4 w-4" />
                API 选择
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>选择接口</Label>
                <SearchableSelect
                  className="w-full"
                  value={currentApi?.id}
                  onChange={(value) => value && handleApiChange(value)}
                  options={apis.map((api) => ({
                    value: api.id,
                    label: `${api.desc} - ${api.path}`,
                  }))}
                  placeholder="选择API..."
                  searchPlaceholder="搜索API..."
                  emptyText="未找到API"
                  allowClear={false}
                />
              </div>

              {currentApi && (
                <>
                  {currentApi.params && currentApi.params.length > 0 && (
                    <div className="border-t pt-4 space-y-3">
                      <h3 className="font-medium text-sm">请求参数</h3>
                      {currentApi.params.map((param) => (
                        <div key={param.param} className="space-y-1">
                          <Label className="text-xs">
                            {param.desc || param.param}
                            {param.default !== undefined && (
                              <span className="text-muted-foreground ml-1">
                                (默认: {param.default})
                              </span>
                            )}
                          </Label>
                          <Input
                            type={param.type === 'number' ? 'number' : 'text'}
                            value={formData[param.param] || ''}
                            onChange={(e) => {
                              const value = param.type === 'number'
                                ? Number(e.target.value)
                                : e.target.value
                              handleFieldChange(param.param, value)
                            }}
                            placeholder={`输入 ${param.param}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <Button
                    className="w-full"
                    onClick={handleExecute}
                    disabled={loading}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    {loading ? '执行中...' : '执行'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Result Display */}
        <div className="col-span-7">
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="text-base">执行结果</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-muted rounded-lg p-4 max-h-[600px] overflow-auto">
                {Object.keys(result).length > 0 ? (
                  <JsonView
                    data={result}
                    shouldExpandNode={allExpanded}
                    style={{
                      ...darkStyles,
                      container: 'font-mono text-sm',
                      label: 'text-sky-400 font-semibold',
                      nullValue: 'text-gray-500',
                      undefinedValue: 'text-gray-500',
                      stringValue: 'text-emerald-400',
                      booleanValue: 'text-purple-400',
                      numberValue: 'text-amber-400',
                      otherValue: 'text-gray-400',
                      punctuation: 'text-gray-600',
                      collapseIcon: 'text-gray-500 hover:text-gray-300 cursor-pointer select-none',
                      expandIcon: 'text-gray-500 hover:text-gray-300 cursor-pointer select-none',
                    }}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    选择API并执行后，结果将显示在这里
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Code Editor Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">代码测试</CardTitle>
          <Button variant="outline" size="sm" onClick={handleCacheCode}>
            <Save className="mr-2 h-4 w-4" />
            缓存代码
          </Button>
        </CardHeader>
        <CardContent>
          <CodeEditor
            isTest
            value={testCode}
            onChange={setTestCode}
            language="python"
          />
        </CardContent>
      </Card>
    </div>
  )
}
